import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { useSelector } from "react-redux";
import ModernEmployeeSelector from "../../../components/utils/Selector/ModernEmployeeSelector";
import { PeriodSelector } from "../../../components/dashboard/hours/period_selector";
import { AbsenceStatsSection } from "../../../components/dashboard/absences/absence-stats-section";
import { AbsencesStacklist } from "../../../components/dashboard/absences/absences-stacklist";
import { startOfYear, endOfYear, format } from "date-fns";
import { exportAbsenceSummaryPDF } from "../../../api/absence/absenece";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

export const AbsenceOverview = () => {
  // Check if user can view absences (admin access)
  const isAdmin = useUserHasAccess({ role: "absence", action: "read" });
  const currentUser = useSelector((state) => state.session.currentUser);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial values from URL params or defaults
  // For admin: allow viewing any user (null means show selector)
  // For non-admin: always use current user
  const initialUserId = searchParams.get("user_id") || (isAdmin ? null : currentUser?.id);
  const initialFrom = searchParams.get("from") || format(startOfYear(new Date()), "yyyy-MM-dd");
  const initialTo = searchParams.get("to") || format(endOfYear(new Date()), "yyyy-MM-dd");
  
  // Initialize user state - if we have a user_id from URL, use it, otherwise use currentUser
  const [user, setUser] = useState(() => {
    if (initialUserId) {
      return { id: parseInt(initialUserId) };
    }
    return currentUser;
  });
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [period, setPeriod] = useState({
    periodType: "year",
    from: initialFrom,
    to: initialTo,
  });

  // Update URL params when user or period changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (user?.id) {
      params.set("user_id", user.id.toString());
    }
    if (period.from) {
      params.set("from", period.from);
    }
    if (period.to) {
      params.set("to", period.to);
    }
    if (period.periodType) {
      params.set("periodType", period.periodType);
    }
    setSearchParams(params, { replace: true });
  }, [user, period, setSearchParams]);

  // Fetch user details if we only have ID from URL
  useEffect(() => {
    if (initialUserId && (!user?.displayTitle || user?.id !== parseInt(initialUserId))) {
      // User ID is set but we don't have full user object - fetch it
      import("../../../api/users").then(({ fetchUser }) => {
        fetchUser(parseInt(initialUserId), { fields: "id,name,displayTitle,email" })
          .then((response) => {
            if (response?.data) {
              setUser(response.data);
            }
          })
          .catch((error) => {
            console.error("Error fetching user:", error);
          });
      });
    }
  }, [initialUserId]);

  function Content() {
    return (
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          {/* Period Selector */}
          <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
            <div className="bg-white px-4 py-5 sm:px-6">
              <PeriodSelector
                periodType={period.periodType}
                from={period.from}
                to={period.to}
                onChange={(newPeriod) => {
                  setPeriod(newPeriod);
                }}
              />
            </div>
          </div>

          {/* Employee Selector */}
          <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
            <div className="bg-white px-4 py-5 sm:px-6">
              {isAdmin ? (
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ansatt
                  </label>
                  <button
                    onClick={() => setSelectorOpen(true)}
                    className="w-full text-left px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {user?.displayTitle || "Velg ansatt"}
                  </button>
                  {selectorOpen && (
                    <div className="fixed inset-0 z-[9998] overflow-y-auto">
                      <div className="flex min-h-screen items-center justify-center p-4">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectorOpen(false)} />
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden z-[9999]">
                          <ModernEmployeeSelector
                            open={selectorOpen}
                            selectedEmployees={user && user.id ? [user] : []}
                            onChange={(employees) => {
                              if (employees.length > 0) {
                                setUser(employees[0]);
                              } else {
                                setUser(currentUser);
                              }
                            }}
                            onClose={() => setSelectorOpen(false)}
                            multiple={false}
                            filters={[{ name: "EMPLOYEE", value: true }]}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ansatt
                  </label>
                  <span className="text-lg font-medium text-gray-900">{user?.displayTitle || currentUser?.displayTitle}</span>
                </div>
              )}
            </div>
          </div>

          <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
            <AbsenceStatsSection user={user} period={period} />
            <div className="mt-5" />
            <AbsencesStacklist user={user} period={period} />
          </div>
        </div>
      </div>
    );
  }

  const handleExportPDF = async () => {
    if (isExportingPDF) return; // Prevent multiple clicks
    
    setIsExportingPDF(true);
    try {
      const params = {
        user_id: user?.id,
        from: period.from,
        to: period.to,
      };
      
      const result = await exportAbsenceSummaryPDF(params);
      
      if (result.success && result.data) {
        // Check if result.data is a Blob
        let blob;
        if (result.data instanceof Blob) {
          blob = result.data;
        } else {
          // If it's not a Blob, try to create one
          blob = new Blob([result.data], { type: 'application/pdf' });
        }
        
        // Validate PDF blob
        if (blob.size < 100) {
          throw new Error("PDF-filen er for liten eller ugyldig");
        }
        
        // Create blob URL and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `fraværsoversikt_${user?.id || 'alle'}_${period.from}_${period.to}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("PDF ble eksportert");
      } else {
        const errorMsg = result.errors?.join(", ") || "Kunne ikke eksportere PDF";
        toast.error(errorMsg);
        console.error("PDF export error:", result);
      }
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error(error.message || "Kunne ikke eksportere PDF");
    } finally {
      setIsExportingPDF(false);
    }
  };

  const headerActions = [
    {
      component: null,
      label: isExportingPDF ? "Eksporterer..." : "Eksporter PDF",
      description: "Eksporter fraværsoversikt som PDF",
      path: null,
      action: handleExportPDF,
      icon: DocumentArrowDownIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: false,
      disabled: isExportingPDF,
    },
  ];

  return (
    <>
      <DashboardHeader subtitle="" title="Fravær - Oversikt" actions={headerActions}>
        <Content />
      </DashboardHeader>
    </>
  );
};
